import { MultiTabQuestionnaire } from "@smartintegrity/questionnaire";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useMemo } from "react";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { Col, Row } from "reactstrap";
import { API_BASE_URL } from "src/modules/data-protection/constants";
import { mapAnswersToSave } from "src/modules/data-protection/helpers/questionnaire";
import AssessmentPagesLayout from "../../../components/AssessmentPagesLayout";
import { createQuestionnaireAiSuggestionExtension } from "../../../components/QuestionnaireAiSuggestionExtension";
import { createQuestionnaireAssessmentGapExtension } from "../../../components/QuestionnaireAssessmentGapExtension";
import { composeQuestionExtensions } from "../../../components/composeQuestionExtensions";

const QuestionsLoadingPlaceholder = () => {
    return (
        <div>
            <Row className="mb-2">
                <Col lg="12">
                    <p style={{ width: "100%", height: "55px" }} className="dt-field dt-skeleton" />
                </Col>
            </Row>
        </div>
    );
};

const FriaQuestionnaire = ({
    questionsQuery,
    t,
    token,
    softwareId,
    friaReportCaseId,
    onValidSubmit,
    onProgressChange
}) => {
    const updateQuestionnaireMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await axios.post(
                `${API_BASE_URL}/report.update`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            return response.data;
        },
        onSuccess({ data }) {
            toast(t("Report Submitted"), {
                type: "success"
            });

            onValidSubmit && onValidSubmit(data);
        },
        onError(error) {
            if (process.env.NODE_ENV === "development") console.error(error);
            toast(t("Error!"), {
                type: "error"
            });
        }
    });

    const handleSubmitQuestionnaire = useCallback((payload) => {
        updateQuestionnaireMutation.mutate({
            questionAnswers: mapAnswersToSave(
                questionsQuery.data?.rawQuestions,
                payload
            ),
            software: softwareId,
            questionnaireType: "fria_assessment",
            hardware: null,
            reportCase: friaReportCaseId
        });
    }, [
        questionsQuery.data?.rawQuestions,
        updateQuestionnaireMutation,
        softwareId,
        friaReportCaseId
    ]);

    const settings = useMemo(() => {
        return {
            tabSettings: {
                showNumber: true,
                showProgressBar: true
            },
            panelSettings: {
                showNumber: true,
                showProgressBar: false,
                prefix: ""
            },
            /**
             * The layout renders Back / Next / Save below the panel, outside
             * the library's form.
             */
            buttonsSettings: {
                submit: {
                    display: false
                },
                back: {
                    display: false
                }
            },
            fileUploaderOptions: {
                deleteRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({ file }) => {
                        return `${API_BASE_URL}/attachment/${file.id}/delete`;
                    }
                },
                uploadRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: () => {
                        return `${API_BASE_URL}/report.uploadAttachment`;
                    }
                }
            },
            questions: {
                showNumber: true
            },
            pagesLayoutContext: {
                navigationTitle: "FRIA Sections",
                onProgressChange,
                isSaving: updateQuestionnaireMutation.isLoading
            }
        };
    }, [
        token,
        updateQuestionnaireMutation.isLoading,
        onProgressChange
    ]);

    const questionnaireComponents = useMemo(() => {
        const QuestionExtension = composeQuestionExtensions(
            createQuestionnaireAiSuggestionExtension(),
            createQuestionnaireAssessmentGapExtension()
        );

        return {
            PagesLayout: AssessmentPagesLayout,
            ...(QuestionExtension ? { QuestionExtension } : {})
        };
    }, []);

    if (questionsQuery.isFetching) {
        return <QuestionsLoadingPlaceholder />;
    }

    return (
        <MultiTabQuestionnaire
            questionnaireUniqueId="fria-questionnaire"
            className="gdpr"
            questions={questionsQuery.data?.mappedQuestions || []}
            defaultValues={questionsQuery.data?.mappedAnswers || []}
            settings={settings}
            components={questionnaireComponents}
            onSubmit={handleSubmitQuestionnaire}
        />
    );
};

export default withNamespaces()(
    connect((state) => {
        const { token } = state.Login;

        return {
            token
        };
    })(FriaQuestionnaire)
);
